import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataService from '../../XXX/DataService';
import './ChampionNotFound.css';

interface ChampionNotFoundProps {
  searchedName: string;
}

interface ChampionEntry {
  id: string;
  name: string;
  imageUrl: string;
}

const ChampionNotFound: React.FC<ChampionNotFoundProps> = ({ searchedName }) => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<ChampionEntry[]>([]);
  const [championsList, setChampionsList] = useState<ChampionEntry[]>([]);
  const [similarChampions, setSimilarChampions] = useState<ChampionEntry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch champion list on mount
  useEffect(() => {
    const fetchChampions = async () => {
      try {
        const version = await DataService.fetchLatestVersion();
        const response = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
        );
        const data = await response.json();

        const championsData: ChampionEntry[] = Object.values(data.data).map((champion: any) => ({
          id: champion.id,
          name: champion.name,
          imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${champion.id}.png`,
        }));

        setChampionsList(championsData);

        // Find similar champions based on the searched name (normalized)
        const normalizedSearch = searchedName.toLowerCase().replace(/['\.\s-]/g, '');
        const similar = championsData
          .filter(c => {
            const normName = c.name.toLowerCase().replace(/['\.\s-]/g, '');
            const normId = c.id.toLowerCase().replace(/['\.\s-]/g, '');
            return normName.includes(normalizedSearch) ||
              normalizedSearch.includes(normName) ||
              normId.includes(normalizedSearch) ||
              normalizedSearch.includes(normId) ||
              levenshteinDistance(normName, normalizedSearch) <= 3 ||
              levenshteinDistance(normId, normalizedSearch) <= 3;
          })
          .slice(0, 5);
        setSimilarChampions(similar);
      } catch (error) {
        console.error('Error fetching champions list:', error);
      }
    };
    fetchChampions();
  }, [searchedName]);

  /** Normalize for fuzzy search: strip apostrophes, periods, spaces */
  const normalizeForSearch = (str: string): string => {
    return str.toLowerCase().replace(/['\.\s-]/g, '');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setSearchInput(input);

    if (input.length > 0) {
      const normalizedInput = normalizeForSearch(input);
      const filtered = championsList
        .filter(c => {
          const normalizedName = normalizeForSearch(c.name);
          const normalizedId = normalizeForSearch(c.id);
          return normalizedName.includes(normalizedInput) || normalizedId.includes(normalizedInput);
        })
        .sort((a, b) => {
          const normA = normalizeForSearch(a.name);
          const normB = normalizeForSearch(b.name);
          const aStartsWith = normA.startsWith(normalizedInput);
          const bStartsWith = normB.startsWith(normalizedInput);
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          return a.name.localeCompare(b.name);
        })
        .slice(0, 8);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  const handleChampionSelect = (champion: ChampionEntry) => {
    navigate(`/champions/${champion.id}`);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="champion-not-found">
      <div className="not-found-content">
        <div className="not-found-icon">?</div>
        <h1 className="not-found-title">Champion Not Found</h1>
        <p className="not-found-message">
          "<span className="searched-name">{searchedName}</span>" doesn't match any known champion.
        </p>

        {similarChampions.length > 0 && (
          <div className="similar-champions">
            <h3>Did you mean?</h3>
            <div className="similar-list">
              {similarChampions.map(champion => (
                <button
                  key={champion.id}
                  className="similar-champion-btn"
                  onClick={() => handleChampionSelect(champion)}
                >
                  <img src={champion.imageUrl} alt={champion.name} />
                  <span>{champion.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="not-found-search">
          <h3>Search for a champion</h3>
          <div className="search-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a champion name..."
              value={searchInput}
              onChange={handleInputChange}
              className="not-found-search-input"
            />
            {suggestions.length > 0 && (
              <ul className="not-found-suggestions">
                {suggestions.map(champion => (
                  <li
                    key={champion.id}
                    className="not-found-suggestion-item"
                    onClick={() => handleChampionSelect(champion)}
                  >
                    <img src={champion.imageUrl} alt={champion.name} />
                    <span>{champion.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <button className="go-home-btn" onClick={handleGoHome}>
          Back to Home
        </button>
      </div>
    </div>
  );
};

/** Simple Levenshtein distance for fuzzy matching */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

export default ChampionNotFound;
